import React, { Fragment, useCallback, useEffect, useState } from "react";
import DefaultButton from "../components/buttons/defaultButton";
import DefaultInput from "../components/inputs/defaultInput";
import StepIndicator from "../components/stepIndicator/StepIndicator";
import thumbup from "../assets/images/thumb-up.png";
import { FiCheck } from "react-icons/fi";
import bydo from "../assets/images/bydologo.png";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../components/skeleton/loading";
import * as Yup from "yup";
import { useFormik } from "formik";
import { _transformPayload, _userRole } from "../../utils/common";
import { updateWorkspace } from "../../services/workspace/workspace.service";
import toast, { Toaster } from "react-hot-toast";
import DefaultTextarea from "../components/inputs/defaultTextarea";
import SimpleFileUploader from "../components/inputs/simpleFileUploader";
import SessionForm from "../components/forms/configuration/sessionForm";
import useUserRole from "../../hooks/useUserRole";
import { _fetchUserData, _logoutUser } from "../../store/actions/user";
import { _updateUserData } from "../../store/slices/user.slice";
import { getPseudoDeviceId } from "../components/sidebar/components/workspaceDropdown";

const steps = [
  { title: "Customize your workspace" },
  { title: "Fill your contact details" },
  { title: "Create Academic Session" },
  "Step 4",
];

const formatMinutesToHours = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours} hours ${mins} minutes`;
};

const validationSchema = Yup.object().shape({
  logo: Yup.object(),
  workspace_name: Yup.string().trim().required("School Name is required"),
  tagline: Yup.string().trim(),
  phone: Yup.string().trim().required("Phone Number is required"),
  alternate_phone: Yup.string()
    .trim()
    .matches(/^\d+$/, "Alternate Phone must be numeric")
    .nullable(),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  alternate_email: Yup.string()
    .email("Invalid alternate email address")
    .nullable(),
  website: Yup.string().trim(),
  address: Yup.string().trim().required("Address is required"),
  ws_start: Yup.string()
    .required("Workspace start time is required"),
  ws_end: Yup.string()
    .required("Workspace end time is required")
    .test("ws-time-diff", "Start and end difference cannot be more than 12 hours", function (value) {
      const { ws_start } = this.parent;
      if (ws_start && value) {
        const [startHour, startMinute] = ws_start.split(":").map(Number);
        const [endHour, endMinute] = value.split(":").map(Number);

        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        let diffMinutes = endTotalMinutes - startTotalMinutes;
        if (diffMinutes < 0) diffMinutes += 24 * 60; // Handle overnight shift

        return diffMinutes <= 720; // 12 hours = 720 minutes
      }
      return true;
    }),
  ws_minutes: Yup.number()
    .required("Working minutes is required")
    .min(1, "Working minutes must be at least 1 minute") // Ensures minimum of 1 minute
    .max(720, "Working minutes cannot be more than 12 hours (720 minutes)"),
  ws_relax_time: Yup.number()
    .max(60, "Relaxation time cannot be more than 1 hour (60 minutes)"),
});

const OnboardingRoute = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, loading: userLoading } = useSelector((state) => state.user);
  const user = useSelector((state) => state?.user?.userInfo?.user);
  const [currentStep, setCurrentStep] = useState(0);
  const role = useUserRole();
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAuthenticated) {
        navigate('/auth/login');
        return;
      }

      const isWorkspaceAdmin = role === _userRole.WORKSPACE_ADMIN;
      const isOnboarding = user?.onboarding;

      if (!isWorkspaceAdmin || isOnboarding) {
        navigate('/');
      }
    }, 500); // delay in milliseconds (adjust as needed)

    return () => clearTimeout(timer); // clean up
  }, [isAuthenticated, role, user, navigate]);

  const handleSkip = () => {
    setCurrentStep(2);
  };

  const handleLogout = () => {
    setLoading(true);
    const device_id = getPseudoDeviceId();
    dispatch(_logoutUser({ device_id }, (error, response) => {
      setLoading(false);
      if (error) {
        return toast.error("Failed to logout!");
      }
      navigate("/auth/login");
    }));
  };

  return (
    <div className="flex flex-row gap-4 h-screen w-full items-center justify-center bg-white p-6">
      <div className="w-[500px] flex flex-col gap-2 p-6 items-start justify-between bg-cyan-50 h-[calc(100vh-40px)] rounded-md relative ">
        <div className="flex flex-col gap-8 items-start">
          <img src={bydo} alt="Bydo" className="h-12 w-36" />
          <StepIndicator
            steps={steps
              .filter((_, index) => index !== 3)
              .map((step, index) => ({
                id: index + 1,
                label: step.title,
              }))}
            currentStep={currentStep + 1}
            orientation="vertical"
          />
        </div>

        <div className="flex gap-2 items-center w-full">
          <DefaultButton
            onClick={handleLogout}
            loading={loading}
            name={"Log Out"}
            minWidth={100}
          />
          {currentStep > 0 && currentStep < 3 && (
            <DefaultButton
              variant="outline"
              onClick={handleBack}
              name={"Back"}
              minWidth={100}
            />
          )}
        </div>

      </div>

      <div className="w-full flex flex-col gap-6 items-center justify-center">
        {/* Step Content */}
        <div className="w-full max-w-xl rounded-lg flex flex-col items-center">
          {userLoading || !isAuthenticated ?
            <Loading />
            :
            <Fragment>
              {currentStep === 0 || currentStep === 1 ?
                <DetailCard
                  handleNext={handleNext}
                  handleSkip={handleSkip}
                  currentStep={currentStep}
                /> : null}
              {currentStep === 2 && <SessionCard handleNext={handleNext} />}
              {currentStep === 3 && <Congratulations />}
            </Fragment>}
        </div>
        {/* Navigation Buttons */}
        <div className="flex flex-row items-start justify-start gap-2">
          {currentStep === 3 && (
            <DefaultButton
              onClick={() => {
                dispatch(_updateUserData());
                navigate("/")
              }}
              name="Go to Dashboard"
              minWidth={100}
            />
          )}
        </div>
      </div>
      <Toaster />
    </div>
  );
};

const DetailCard = ({ currentStep, handleNext = () => null, handleSkip = () => null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workspaceData, setWorkspaceData] = useState({
    logo: "",
    alternate_logo: "",
    name: "",
    tagline: "",
    phone: "",
    alternate_phone: "",
    email: "",
    alternate_email: "",
    website: "",
    address: "",
    latitude: "",
    longitude: "",
    is_email_notification: true,
    is_sms_notification: false,
    is_push_notification: true,
    linkedin: "",
    instagram: "",
    facebook: "",
    twitter: "",
    ws_start: "",
    ws_end: "",
    ws_minutes: 0,
    ws_relax_time: 0,
  });

  const handleError = (error) => {
    const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
    toast.error(`Failed to update workspace! ${errorMessage}`);
  }

  const {
    handleSubmit,
    handleChange,
    values,
    touched,
    errors,
    setFieldValue
  } =
    useFormik({
      initialValues: {
        logo: workspaceData?.logo || "",
        alternate_logo: workspaceData?.alternate_logo || "",
        workspace_name: workspaceData?.name || "",
        tagline: workspaceData?.tagline || "",
        phone: workspaceData?.phone || "",
        alternate_phone: workspaceData?.alternate_phone || "",
        email: workspaceData?.email || "",
        alternate_email: workspaceData?.alternate_email || "",
        website: workspaceData?.website || "",
        address: workspaceData?.address || "",
        latitude: workspaceData?.latitude || "",
        longitude: workspaceData?.longitude || "",
        is_email_notification: workspaceData?.is_email_notification ?? true,
        is_sms_notification: false,
        is_push_notification: workspaceData?.is_push_notification ?? true,
        linkedin: workspaceData?.linkedin || "",
        instagram: workspaceData?.instagram || "",
        facebook: workspaceData?.facebook || "",
        twitter: workspaceData?.twitter || "",
        ws_start: workspaceData?.ws_start || "",
        ws_end: workspaceData?.ws_end || "",
        ws_minutes: workspaceData?.ws_minutes || 0,
        ws_relax_time: workspaceData?.ws_relax_time || 0,
      },
      validationSchema,
      enableReinitialize: true,
      onSubmit: async (values) => {
        try {
          setIsSubmitting(true);
          const payload = _transformPayload(values);
          if (workspaceData?.uuid) {
            try {
              await updateWorkspace(payload, workspaceData?.uuid);
              toast.success("Workspace updated successfully!");
              handleNext()
            } catch (error) {
              handleError(error);
            }
          } else {
            toast.error("Failed to update workspace!.");
          }
        } catch (error) {
          handleError(error);
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const toggleErrors = () => {
    if (errors?.ws_start || errors?.ws_end || errors?.ws_minutes || errors?.address) {
      toast.error("Failed to save the details. Please check the 'Customize Your Workspace' tab for any possible errors.");
    }
  }

  const handleSwitch = () => {
    if (currentStep == 1) {
      handleSubmit()
      toggleErrors()
    } else {
      handleNext();
    }
  }

  useEffect(() => {
    if (values.ws_start && values.ws_end) {
      const [startHour, startMinute] = values.ws_start.split(":").map(Number);
      const [endHour, endMinute] = values.ws_end.split(":").map(Number);

      const startTotalMinutes = startHour * 60 + startMinute;
      const endTotalMinutes = endHour * 60 + endMinute;

      let diffMinutes = endTotalMinutes - startTotalMinutes;
      if (diffMinutes < 0) diffMinutes += 24 * 60; // Handle overnight case

      // Ensure the working minutes do not exceed 12 hours (720 minutes)
      const finalMinutes = Math.min(diffMinutes, 720);

      setFieldValue("ws_minutes", finalMinutes);
    }
  }, [values.ws_start, values.ws_end]);

  return (
    <Fragment>
      {currentStep == 1 ?
        <div className="w-full flex flex-col gap-8 mx-auto">
          <div className="flex flex-col gap-0">
            <h2 className="text-theme text-2xl leading-2 font-semibold text-center">
              Fill Your Workspace Contact Details
            </h2>
            <p className="text-cs text-textLight font-nornal text-center">
              Setup your workspace's contact details for easy communication.
            </p>
          </div>
          <form className="w-full flex flex-col gap-2 mx-auto">

            <DefaultInput
              type="number"
              error={
                touched.phone && errors.phone
                  ? errors.phone
                  : ""
              }
              label={"Phone Number"}
              name="phone"
              onChange={handleChange}
              value={values.phone}
              readOnly={true}
              required
            />

            <DefaultInput
              type="number"
              error={
                touched.alternate_phone && errors.alternate_phone
                  ? errors.alternate_phone
                  : ""
              }
              label={"Alternate Phone Number"}
              name="alternate_phone"
              onChange={handleChange}
              value={values.alternate_phone}
            />
            <DefaultInput
              error={touched.email && errors.email ? errors.email : ""}
              label={"Email"}
              name="email"
              onChange={handleChange}
              value={values.email}
              required
              readOnly={true}
            />

            <DefaultInput
              error={touched.alternate_email && errors.alternate_email ? errors.alternate_email : ""}
              label={"Alternate Email"}
              name="alternate_email"
              onChange={handleChange}
              value={values.alternate_email}
            />
          </form>
        </div> :
        <div className="w-full flex flex-col gap-8 mx-auto">
          <div className="flex flex-col gap-0">
            <h2 className="text-theme text-2xl leading-2 font-semibold text-center">
              Customize Your Workspace
            </h2>
            <p className="text-cs text-textLight font-nornal text-center">
              Setup your workspace details to personalize your campus experience.
            </p>
          </div>
          <form className="flex flex-col gap-2">
            <div className="flex row items-center gap-2 border rounded-md p-2">
              <SimpleFileUploader
                name={"logo"}
                custom
                id="logo"
                onChange={(file) => {
                  setFieldValue("logo", file);
                }}
                value={values.logo}
                error={touched.logo && errors.logo ? errors.logo : ""}
                accept={["png"]}
                info={"PNG format with a maximum file size of 10 MB and a required 200x200 aspect ratio."}
                sub_heading="Format: PNG, Dimensions: 200x200 px."
                required
              />
            </div>
            <DefaultInput
              error={
                touched.workspace_name && errors.workspace_name
                  ? errors.workspace_name
                  : ""
              }
              label={"WorkSpace/Campus Name"}
              name="workspace_name"
              onChange={handleChange}
              value={values.workspace_name}
              required
              readOnly={true}
            />
            <div className="flex items-center gap-2">
              <DefaultInput
                error={
                  touched.tagline && errors.tagline ? errors.tagline : ""
                }
                label={"Tagline"}
                name="tagline"
                onChange={handleChange}
                value={values.tagline}
              />

              <DefaultInput
                error={
                  touched.website && errors.website ? errors.website : ""
                }
                label={"Website"}
                name="website"
                onChange={handleChange}
                value={values.website}
              />
            </div>
            <div className="flex items-center gap-2">
              <DefaultInput
                label={"Workspace Start Time"}
                value={values.ws_start}
                name={"ws_start"}
                onChange={handleChange}
                type="time"
                className="bg-white border border-transparent hover:border-borderColor"
                error={
                  touched.ws_start && errors.ws_start
                    ? errors.ws_start
                    : false
                }
                required
              />
              <DefaultInput
                label={"Workspace End Time"}
                value={values.ws_end}
                name={"ws_end"}
                onChange={handleChange}
                type="time"
                className="bg-white border border-transparent hover:border-borderColor"
                error={
                  touched.ws_end && errors.ws_end
                    ? errors.ws_end
                    : false
                }
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <DefaultInput
                required
                label={`Working Hours ${!values.ws_minutes ? "(minutes)" : `(${formatMinutesToHours(values.ws_minutes)})`
                  }`}
                name="ws_minutes"
                number
                onChange={handleChange}
                value={values.ws_minutes}
                error={
                  touched.ws_minutes && errors.ws_minutes ? errors.ws_minutes : ""
                }
              />

              <DefaultInput
                label={"Attendance Relax Time (minutes)"}
                name="ws_relax_time"
                number
                onChange={handleChange}
                value={values.ws_relax_time}
                error={
                  touched.ws_relax_time && errors.ws_relax_time ? errors.ws_relax_time : ""
                }
              />
            </div>

            <DefaultTextarea
              error={
                touched.address && errors.address ? errors.address : ""
              }
              label={"Address"}
              name="address"
              onChange={handleChange}
              value={values.address}
              required
            />
          </form>
        </div>}
      <div className="flex flex-row items-start justify-start gap-2 mt-6">
        <DefaultButton
          onClick={handleSwitch}
          name={currentStep == 1 ? "Save" : "Continue"}
          loading={isSubmitting}
          minWidth={100}
        />
        <DefaultButton
          onClick={handleSkip}
          name="Skip"
          minWidth={100}
          variant="outline"
        />
      </div>
    </Fragment>
  )
}

const SessionCard = ({ handleNext = () => null }) => (
  <div className="w-full flex flex-col gap-8 mx-auto">
    <div className="flex flex-col gap-0">
      <h2 className="text-theme text-2xl leading-2 font-semibold text-center">
        Create Academic Session
      </h2>
      <p className="text-cs text-textLight font-nornal text-center">
        Define your session's start and end dates for streamlined management.
      </p>
    </div>
    <SessionForm handleNext={handleNext} onBoard={true} />
  </div>
);

const Congratulations = () => (
  <div className="w-full flex flex-col justify-center items-center gap-4 mx-auto">
    <img src={thumbup} alt="Congratulation" className="h-48 w-48" />
    <div className="flex flex-col gap-0">
      <h2 className="text-theme text-2xl leading-2 font-semibold text-center">
        Congratulations! You're Ready to Go!
      </h2>
      <p className="text-cs text-textLight font-nornal text-center">
        Your workspace is set up, ready to manage effortlessly ahead.
      </p>
    </div>
  </div>
);

export default OnboardingRoute;
